import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isWorkingDay } from '@rundown/domain';
import { holidaysQueryOptions, type HolidayRow } from './api';

export interface WorkspaceHolidays {
  /** Just the dates, for the pure working-day maths in @rundown/domain. */
  readonly dates: readonly string[];
  /** The holidays falling inside a sprint, named, for the warning chip. */
  readonly within: (startsOn: string, endsOn: string) => readonly HolidayRow[];
}

/**
 * The workspace's holidays, in the two shapes screens actually need.
 *
 * A failed or empty fetch yields no holidays rather than an error: every
 * caller is decorating a sprint with "and these days are off", and a plan that
 * renders without the decoration is far better than one that does not render.
 */
export function useHolidays(workspaceId: string): WorkspaceHolidays {
  const query = useQuery(holidaysQueryOptions(workspaceId));

  return useMemo(() => {
    const observed = (query.data ?? []).filter((holiday) => holiday.observed);
    // Two calendars can both claim 25 December; the day is only lost once.
    // eslint-disable-next-line unicorn/no-array-sort -- sorting the array the Set spread just built
    const dates = [...new Set(observed.map((holiday) => holiday.onDate))].sort();

    return {
      dates,
      within: (startsOn, endsOn) =>
        observed
          .filter(
            (holiday) =>
              holiday.onDate >= startsOn &&
              holiday.onDate <= endsOn &&
              // A holiday landing on a Saturday costs the sprint nothing, so
              // reporting it as a lost day would be wrong twice over.
              isWorkingDay(holiday.onDate, []),
          )
          // eslint-disable-next-line unicorn/no-array-sort -- sorting the array `filter` just built
          .sort((a, b) => a.onDate.localeCompare(b.onDate)),
    };
  }, [query.data]);
}
